import { useEffect, useRef } from "react";

export interface QueueHotkeyHandlers {
  /** Pil høyre eller Enter: primærhandlingen for kortet. */
  onPrimary?: () => void;
  /** Bare pil høyre (ikke Enter): handling som ikke kan angres, som å ta ansvar for et signal. */
  onClaim?: () => void;
  /** Pil venstre: «Ikke for meg» på signalkort. */
  onSecondary?: () => void;
  /** 1: E-post. */
  onEmail?: () => void;
  /** 2: Tok det på telefon. */
  onPhone?: () => void;
  /** 3: Utsett. */
  onPostpone?: () => void;
  /** 4: Avslutt. */
  onClose?: () => void;
}

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** Elementer der Enter og pil høyre allerede betyr noe (aktivere, bytte fane). */
const INTERACTIVE = 'button, a, summary, [role="tab"], [role="button"], [role="menuitem"], [role="option"]';
const ACTIVATION_KEYS = new Set(["Enter", "ArrowRight", "ArrowLeft"]);

/** Ingen hotkeys mens brukeren skriver, har en dialog åpen eller holder en modifikator. */
export function shouldIgnoreHotkey(event: KeyboardEvent): boolean {
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return true;
  const target = event.target as HTMLElement | null;
  if (target) {
    if (EDITABLE_TAGS.has(target.tagName) || target.isContentEditable) return true;
    if (target.closest?.('[role="textbox"], [role="combobox"], [role="listbox"]')) return true;
    // Enter på en fokusert knapp eller lenke skal aktivere den, ikke starte primærhandlingen.
    if (ACTIVATION_KEYS.has(event.key) && target.closest?.(INTERACTIVE)) return true;
  }
  return !!document.querySelector('[role="dialog"], [role="alertdialog"]');
}

/**
 * Tastatursnarveier for køen. `Cmd/Ctrl+K` (CommandPalette) har modifikator og
 * kolliderer derfor ikke. Handlerne leses via ref, så lytteren ikke settes opp
 * på nytt ved hver render.
 */
export function useQueueHotkeys(handlers: QueueHotkeyHandlers, enabled = true): void {
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreHotkey(event)) return;
      const h = ref.current;
      let handler: (() => void) | undefined;
      switch (event.key) {
        case "ArrowRight":
          handler = h.onPrimary ?? h.onClaim;
          break;
        case "Enter":
          handler = h.onPrimary;
          break;
        case "ArrowLeft":
          handler = h.onSecondary;
          break;
        case "1":
          handler = h.onEmail;
          break;
        case "2":
          handler = h.onPhone;
          break;
        case "3":
          handler = h.onPostpone;
          break;
        case "4":
          handler = h.onClose;
          break;
      }
      if (!handler) return;
      event.preventDefault();
      handler();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
